import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import LandingFigure, { ScreenshotGallery } from '../components/LandingFigure';
import { SITE_LAST_UPDATED } from '../data/features';
import { longDate } from '../lib/siteDates';
import { getAlternative, allAlternatives } from '../data/alternatives';
import { COMPARE_GUIDES } from '../data/guides';
import { ArrowRight, Check, X, ChevronRight, ArrowRightLeft, Sparkles } from 'lucide-react';

// Render the "winner" cell with a check, and other cells with prose. The
// BusyBee column is always col 1, competitor col 2.
const Cell = ({ value, accent }) => (
  <span className={`inline-flex items-start gap-2 ${accent ? 'text-amber-300' : 'text-gray-300'}`}>
    {accent && <Check className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />}
    <span>{value}</span>
  </span>
);

const AlternativesIndex = () => {
  const { t } = useTranslation(['landing', 'compare']);
  const items = allAlternatives(t);
  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={t('alternatives.index.seo_title')}
        description={t('alternatives.index.seo_description')}
        canonical="/alternatives"
      />
      <Navbar />
      <main className="pt-12 pb-24">
        <section className="max-w-5xl mx-auto px-4 pt-12 pb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-400 mb-6 text-xs font-medium">
            <ArrowRightLeft className="w-3.5 h-3.5" />
            <span>{t('alternatives.badge', { defaultValue: 'Compare' })}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight">
            {t('alternatives.index.headline', { defaultValue: 'BusyBee.Day vs the productivity tools you already know' })}
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl">
            {t('alternatives.index.intro', { defaultValue: 'Honest, up-to-date comparisons - no fake checkmarks. Where the other tool is the right fit, we say so.' })}
          </p>
        </section>

        <section className="max-w-5xl mx-auto px-4 pb-12">
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Comparisons with AI agents and AI work tools (translated, see data/guides.js) */}
            {COMPARE_GUIDES.map((g) => {
              const key = `guide_links.${g.path.slice(1)}`;
              return (
                <Link
                  key={g.path}
                  to={g.path}
                  className="group p-6 bg-white/5 border border-gray-800 rounded-2xl hover:border-amber-500/40 transition-colors"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-wider text-gray-500">{t('alternatives.index.vs_label')}</div>
                    <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-amber-400 transition-colors" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">{t(`${key}.rival`)}</h2>
                  <p className="text-gray-400 text-sm leading-relaxed">{t(`${key}.desc`)}</p>
                </Link>
              );
            })}
            {items.map((alt) => (
              <Link
                key={alt.slug}
                to={`/alternatives/${alt.slug}`}
                className="group p-6 bg-white/5 border border-gray-800 rounded-2xl hover:border-amber-500/40 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-xs uppercase tracking-wider text-gray-500">{t('alternatives.index.vs_label', { defaultValue: 'BusyBee.Day vs' })}</div>
                  <ArrowRight className="w-4 h-4 text-gray-600 group-hover:text-amber-400 transition-colors" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">{alt.name}</h2>
                <p className="text-gray-400 text-sm leading-relaxed">{alt.tagline}</p>
                <div className="text-xs text-gray-500 mt-4">{alt.paid_from}</div>
              </Link>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const AlternativeDetail = ({ alt }) => {
  const { t, i18n } = useTranslation(['landing', 'compare']);
  const heroShot = alt.screenshots?.[0];
  const updated = longDate(SITE_LAST_UPDATED, i18n.resolvedLanguage || 'en');

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <SEO
        title={alt.seoTitle}
        description={alt.seoDescription}
        keywords={alt.keywords}
        canonical={`/alternatives/${alt.slug}`}
        image={heroShot?.src}
        faqItems={alt.faq}
        breadcrumbs={[
          { name: t('pricing.breadcrumb_home'), url: '/' },
          { name: t('alternatives.crumb'), url: '/alternatives' },
          { name: alt.name },
        ]}
      />
      <Navbar />

      <main className="pt-12 pb-24">
        <ScreenshotGallery items={alt.screenshots || []}>

          {/* Hero */}
          <section className="max-w-7xl mx-auto px-4 pt-12 pb-12 sm:pt-16">
            <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
              <Link to="/" className="hover:text-white transition-colors">{t('pricing.breadcrumb_home', { defaultValue: 'Home' })}</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <Link to="/alternatives" className="hover:text-white transition-colors">{t('alternatives.crumb', { defaultValue: 'Alternatives' })}</Link>
              <ChevronRight className="w-3.5 h-3.5" />
              <span className="text-gray-300">{alt.name}</span>
            </nav>

            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 text-amber-400 mb-6 text-xs font-medium">
                  <ArrowRightLeft className="w-3.5 h-3.5" />
                  <span>{t('alternatives.detail.badge', { defaultValue: 'Side-by-side comparison' })}</span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-5 leading-[1.05]">
                  {alt.pageTitle}
                </h1>
                <p className="text-xl text-gray-300 mb-5">{alt.tagline}</p>
                <p className="text-base text-gray-400 leading-relaxed mb-8 max-w-2xl">{alt.positioning}</p>
                <div className="flex flex-wrap gap-3 mb-8">
                  <Link to="/register" className="bg-amber-500 text-black px-6 py-3 rounded-lg font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                    {t('alternatives.detail.cta_start_free', { defaultValue: 'Try BusyBee.Day free' })}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link to="/pricing" className="border border-gray-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-white/5 transition-colors">
                    {t('alternatives.detail.cta_pricing', { defaultValue: 'See pricing' })}
                  </Link>
                </div>
                {/* A "4.8 / 2,400+ reviews" rating used to sit here. No such
                    rating exists. Invented ratings are prohibited outright by
                    Annex I of Directive 2005/29/EC. */}
                <div className="flex items-center gap-6 text-sm text-gray-500">
                  <span>{t('alternatives.detail.last_updated', { defaultValue: 'Last updated' })} {updated}</span>
                </div>
              </div>

              {heroShot && (
                <div className="lg:col-span-6">
                  <LandingFigure
                    src={heroShot.src}
                    alt={heroShot.alt}
                    caption={heroShot.caption}
                    eager
                    frame="hero"
                  />
                </div>
              )}
            </div>
          </section>

          {/* Pricing snapshot */}
          <section className="max-w-5xl mx-auto px-4 py-12">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-6 rounded-2xl border border-amber-500/30 bg-amber-500/5">
                <div className="text-xs uppercase tracking-wider text-amber-400 mb-2 font-semibold">{t('alternatives.detail.busybee', { defaultValue: 'BusyBee.Day' })}</div>
                <div className="text-2xl font-bold text-white mb-1">{alt.busybee_paid_from}</div>
                <div className="text-xs text-gray-500">{t('alternatives.detail.busybee_note', { defaultValue: '1-seat minimum, AI & MCP included' })}</div>
              </div>
              <div className="p-6 rounded-2xl border border-gray-800 bg-white/[0.03]">
                <div className="text-xs uppercase tracking-wider text-gray-500 mb-2 font-semibold">{alt.name}</div>
                <div className="text-2xl font-bold text-white mb-1">{alt.paid_from}</div>
                <div className="text-xs text-gray-500">{alt.free_tier}</div>
              </div>
            </div>
          </section>

          {/* Comparison table */}
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="max-w-3xl mb-8">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('alternatives.detail.section.comparison', { defaultValue: 'Side by side' })}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white">{alt.comparison.title}</h2>
            </div>
            <div className="overflow-x-auto rounded-2xl border border-gray-800 bg-white/[0.02]">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b border-gray-800 bg-white/[0.03]">
                    {alt.comparison.columns.map((col, i) => (
                      <th key={i} className={`px-5 py-4 text-left font-semibold ${i === 1 ? 'text-amber-400' : 'text-gray-300'}`}>
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {alt.comparison.rows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-800 last:border-0 hover:bg-white/[0.02] transition-colors">
                      {row.map((cell, j) => (
                        <td key={j} className={`px-5 py-4 align-top ${j === 0 ? 'text-gray-300 font-medium' : ''}`}>
                          {j === 0 ? cell : <Cell value={cell} accent={j === 1} />}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4">
              {t('alternatives.detail.disclaimer', { defaultValue: 'Pricing and feature data verified against vendor sites on' })} {updated}.
            </p>
          </section>

          {/* Strengths / weaknesses */}
          <section className="max-w-7xl mx-auto px-4 py-12">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-6 rounded-2xl border border-gray-800 bg-white/[0.03]">
                <h3 className="text-xs uppercase tracking-wider text-green-400 font-semibold mb-4 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span>{t('alternatives.detail.section.competitor_strengths', { defaultValue: 'Where {{name}} wins', interpolation: { escapeValue: false } }).replace('{{name}}', alt.name)}</span>
                </h3>
                <ul className="space-y-3">
                  {alt.strengths.map((s, i) => (
                    <li key={i} className="text-gray-300 text-sm leading-relaxed flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="p-6 rounded-2xl border border-gray-800 bg-white/[0.03]">
                <h3 className="text-xs uppercase tracking-wider text-red-400 font-semibold mb-4 flex items-center gap-2">
                  <X className="w-4 h-4" />
                  <span>{t('alternatives.detail.section.competitor_weaknesses', { defaultValue: 'Where {{name}} struggles', interpolation: { escapeValue: false } }).replace('{{name}}', alt.name)}</span>
                </h3>
                <ul className="space-y-3">
                  {alt.weaknesses.map((s, i) => (
                    <li key={i} className="text-gray-300 text-sm leading-relaxed flex items-start gap-2">
                      <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Screenshots (additional) */}
          {alt.screenshots && alt.screenshots.length > 1 && (() => {
            const extras = alt.screenshots.slice(1);
            const isSingle = extras.length === 1;
            return (
              <section className="max-w-7xl mx-auto px-4 py-12">
                <div className={isSingle ? 'max-w-4xl mx-auto' : 'grid md:grid-cols-2 gap-6'}>
                  {extras.map((s, i) => (
                    <LandingFigure key={i} src={s.src} alt={s.alt} caption={s.caption} />
                  ))}
                </div>
              </section>
            );
          })()}

          {/* Switch reasons */}
          <section className="max-w-4xl mx-auto px-4 py-12">
            <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('alternatives.detail.section.switch_label', { defaultValue: 'Should you switch?' })}</div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('alternatives.detail.switch_headline', { defaultValue: 'Reasons people switch from {{name}}', interpolation: { escapeValue: false } }).replace('{{name}}', alt.name)}</h2>
            <p className="text-gray-400 mb-8 text-sm">{alt.best_for}</p>
            <ul className="space-y-3">
              {alt.switch_reasons.map((r, i) => (
                <li key={i} className="flex items-start gap-3 p-4 bg-white/5 rounded-xl border border-gray-800">
                  <Sparkles className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm leading-relaxed">{r}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* FAQ */}
          {alt.faq?.length > 0 && (
            <section className="max-w-4xl mx-auto px-4 py-12">
              <div className="text-xs uppercase tracking-wider text-amber-400 font-semibold mb-2">{t('feature_page.section.faq', { defaultValue: 'FAQ' })}</div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8">{t('alternatives.detail.faq_headline', { defaultValue: 'Common questions about switching' })}</h2>
              <div className="space-y-3">
                {alt.faq.map((item, i) => (
                  <details key={i} className="group bg-white/5 border border-gray-800 rounded-xl overflow-hidden hover:border-gray-700 transition-colors">
                    <summary className="cursor-pointer list-none flex items-start justify-between gap-4 p-5">
                      <h3 className="text-white font-semibold text-base sm:text-lg">{item.question}</h3>
                      <ChevronRight className="w-5 h-5 text-gray-500 group-open:rotate-90 transition-transform flex-shrink-0 mt-0.5" />
                    </summary>
                    <div className="px-5 pb-5 text-gray-400 leading-relaxed text-sm sm:text-base">{item.answer}</div>
                  </details>
                ))}
              </div>
            </section>
          )}

          {/* Other comparisons */}
          <section className="max-w-7xl mx-auto px-4 py-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-8 text-center">{t('alternatives.detail.related_headline', { defaultValue: 'Compare other tools' })}</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {allAlternatives(t).filter((a) => a.slug !== alt.slug).map((other) => (
                <Link
                  key={other.slug}
                  to={`/alternatives/${other.slug}`}
                  className="p-5 bg-white/5 rounded-xl border border-gray-800 hover:border-amber-500/30 transition-colors group"
                >
                  <div className="text-xs uppercase tracking-wider text-gray-500 mb-1">{t('alternatives.index.vs_label', { defaultValue: 'BusyBee.Day vs' })}</div>
                  <div className="text-white font-semibold group-hover:text-amber-400 transition-colors">{other.name}</div>
                  <div className="text-gray-500 text-sm mt-2 line-clamp-2">{other.tagline}</div>
                </Link>
              ))}
            </div>
          </section>

          {/* Final CTA */}
          <section className="max-w-4xl mx-auto px-4 py-16 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              {t('alternatives.detail.final_cta_headline', { defaultValue: 'Try BusyBee.Day free for 14 days' })}
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              {t('alternatives.detail.final_cta_body', { defaultValue: 'No credit card. Bring your data when you are ready - we will not lock you in.' })}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/register" className="bg-amber-500 text-black px-8 py-4 rounded-xl font-semibold hover:bg-amber-400 transition-colors inline-flex items-center gap-2">
                {t('alternatives.detail.cta_start_free', { defaultValue: 'Try BusyBee.Day free' })}
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/pricing" className="border border-gray-700 text-white px-8 py-4 rounded-xl font-medium hover:bg-white/5 transition-colors">
                {t('alternatives.detail.cta_pricing', { defaultValue: 'See pricing' })}
              </Link>
            </div>
          </section>
        </ScreenshotGallery>
      </main>

      <Footer />
    </div>
  );
};

const AlternativeRoute = ({ competitor }) => {
  const { t } = useTranslation(['landing', 'compare']);
  const alt = getAlternative(competitor, t);
  if (!alt) return <Navigate to="/alternatives" replace />;
  return <AlternativeDetail alt={alt} />;
};

const AlternativesPage = () => {
  const { competitor } = useParams();
  if (!competitor) return <AlternativesIndex />;
  return <AlternativeRoute competitor={competitor} />;
};

export default AlternativesPage;
